import React, { useEffect, useRef, useState } from 'react';
import PageHero from '../../../layouts/PageHero';
import Image from 'next/image';
import Link from 'next/link';
import SearchAndFilterBlogs from '../RevampedBlogs/SearchAndFilterBlogs/SearchAndFilterBlogs';
import { AuthorBlogCard } from '../../Author/AuthorBlogCard';
import BlogAuthors from '../RevampedBlogs/BlogAuthors/BlogAuthors';
import Newsletter from '../../../layouts/Newsletter/Newsletter';
import Pagination from '../../../shared-components/Pagination/Pagination';
import scrollToElement from '../../../utils/scrollIntoView';
import { BlogCard } from '../../../types/pages/author';
import { Author, BlogCity } from '../../../pages/blog';
import { BlogCategoryData } from '../../../pages/blog/category/[categoryId]';
import Breadcrumb from '../../../shared-components/Breadcrumb/Breadcrumb';
import { useRouter } from 'next/router';

interface BlogCategoryProps {
  authors: Author[];
  blogs: BlogCard[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => Promise<void>;
  onCityChange: (cityId: string) => Promise<void>;
  cities: BlogCity[];
  blogCategoryData: BlogCategoryData;
}

export default function BlogCategory({
  authors,
  blogs,
  cities,
  currentPage,
  totalPages,
  onPageChange,
  onCityChange,
  blogCategoryData
}: BlogCategoryProps) {
  const router = useRouter();
  const categoryId = router.query.categoryId as string;

  const breadcrumbLinks = [
    { title: 'Home', hrefLink: '/' },
    { title: 'Blogs', hrefLink: '/blog' },
    { title: blogCategoryData?.name, hrefLink: `/blog/category/${categoryId}` }
  ];

  const [searchQuery, setSearchQuery] = useState('');
  const [isInitialRender, setIsInitialRender] = useState(true);
  const blogsDivRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (e: any) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
    } else {
      if (blogsDivRef.current) {
        blogsDivRef.current.scrollTop = 0;
      }
      scrollToElement('blog-cards');
    }
  }, [currentPage]);

  return (
    <div className="flex flex-col gap-10 md:gap-20">
      <div className="w-[90%] md:w-[85%] lg:w-[88%] xl:w-[85%] 2xl:w-[80%] 3xl:w-[85%] 4xl:w-[85%] mx-auto flex flex-col gap-10 md:gap-12">
        <div className="flex flex-col gap-2 lg:gap-5">
          <PageHero
            title={`— ${blogCategoryData?.name} —`}
            snippet={''}
            media={blogCategoryData?.image}
            video={false}
          />

          <div
            className="flex flex-col gap-5 md:gap-7 scroll-mt-10"
            id={'blog-cards'}
          >
            <Breadcrumb
              breadcrumbLinks={breadcrumbLinks}
              alignContent="justify-start"
              isArrowSeparator={true}
            />
            <SearchAndFilterBlogs
              title={`Blogs About ${blogCategoryData?.name}`}
              searchQuery={searchQuery}
              handleInputChange={handleInputChange}
              sliderCategories={cities}
              onCityChange={onCityChange}
            />
          </div>
        </div>
        {blogs?.length > 0 ? (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-[30px] thin-scrollbar overflow-auto md:max-h-[900px]"
            ref={blogsDivRef}
          >
            {blogs?.map((blog: any) => (
              <AuthorBlogCard
                key={blog.id}
                blog={blog}
                authorName={blog.author}
                authorImage={blog.authorImage}
              />
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 mt-8">
            <h2 className="text-xl font-semibold">No Blogs Found</h2>
            <p className="mt-2">
              It seems there are no blogs available for this category at the
              moment. Please check back later for updates!
            </p>
          </div>
        )}
        <div className="flex justify-center">
          {totalPages > 1 && (
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              handlePageChange={onPageChange}
            />
          )}
        </div>
      </div>
      <div className="w-full bg-grayFA">
        <div className="w-[90%] md:w-[85%] lg:w-[88%] xl:w-[85%] 2xl:w-[80%] 3xl:w-[85%] 4xl:w-[85%] mx-auto flex flex-col gap-10 md:gap-12 py-16">
          <BlogAuthors
            authors={authors?.slice().sort((a, b) => {
              return b.image ? 1 : -1;
            })}
          />
        </div>
      </div>
      <Newsletter />
    </div>
  );
}
